import { Divider, IconButton } from '@mui/material';
import AccordionItem from 'components/common/AccordionItem';
import Close from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { Accordion, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Observation } from '../Observation';
import { ObservationItem } from '../Observation/types';
import greenCircleImg from 'assets/images/greencircle.png';
import pendingImg from 'assets/images/pending.png';
import { useEffect, useRef, useState } from 'react';
import Modal from 'components/common/Modal/Modal';
import { usePrevious } from 'hooks/usePrevious';
import { AddObservationProps } from './types';
import './AddObservation.scss';

export const AddObservation: React.FC<AddObservationProps> = ({
  observations,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const bottomRef = useRef<any>(null);
  const itemRefs = useRef<any>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { projectId, observationId } = useParams();
  const initialObservation: ObservationItem = {
    areaOfObservations: { name: '' },
    description: '',
    severity: '',
    images: [],
    locations: [],
    observationType: '',
    isCloseTillRequired: true,
    closeTill: '',
  };

  const formik = useFormik({
    initialValues: {
      observations: [initialObservation] as ObservationItem[],
    },
    onSubmit: () => {
      setShowConfirmation(true);
    },
  });

  const formikValues = usePrevious(formik.values.observations);

  useEffect(() => {
    if (
      observations?.length &&
      formik.values.observations[0].areaOfObservations.name === ''
    ) {
      formik.setFieldValue('observations', observations);
    }
  }, [formik, observations]);

  useEffect(() => {
    if (formikValues.length < formik.values.observations?.length) {
      itemRefs.current[formik.values.observations?.length - 1].click();
      // bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [formikValues, formik.values.observations]);

  const handleAddMoreObservation = () => {
    formik.setFieldValue('observations', [
      ...formik.values.observations,
      initialObservation,
    ]);
  };

  const handleBack = () =>
    navigate(`/project/${projectId}/log-observation/${observationId}/0`);
  const getUpdateFieldHandler =
    (index: number) => (observation: ObservationItem) => {
      const newArray = [...formik.values.observations];
      newArray.splice(index, 1, {
        ...observation,
        markFilled:
          observation.areaOfObservations?.name.length > 0 &&
          observation.observationType.length > 0 &&
          observation.description.length > 0 &&
          observation.locations?.length > 0 &&
          observation.severity?.length > 0 &&
          (!!observation.closeTill || !observation.isCloseTillRequired),
      });
      formik.setFieldValue('observations', newArray);
    };

  const handleSubmit = () => {
    setShowConfirmation(false);
    onSubmit?.({
      observationId,
      projectId,
      body: formik.values.observations.map((ob) => ({
        areaOfObservations: ob.areaOfObservations.name,
        description: ob.description,
        locations: ob.locations,
        images: ob.images,
        observationType: ob.observationType,
        isCloseTillRequired: ob.isCloseTillRequired,
        closeTill: ob.closeTill,
      })),
    });
  };

  const handleClose = () => setShowConfirmation(false);

  const getDeleteObservationHander = (i: number) => () => {
    const newArray = [...formik.values.observations];
    newArray.splice(i, 1);

    formik.setFieldValue('observations', newArray);
  };

  return (
    <form
      className="add-observation d-flex flex-column h-100"
      onSubmit={formik.handleSubmit}
    >
      <Accordion className="flex-grow-1 overflow-auto">
        {formik.values.observations.map((item, i) => (
          <AccordionItem
            eventKey={`ob-${i + 1}`}
            key={i + 1}
            text={`Observation ${i + 1}`}
            leftAction={
              <div ref={(el) => (itemRefs.current[i] = el)}>
                {!item.markFilled ? (
                  <img alt="pending" src={pendingImg} />
                ) : (
                  <img alt="completed" src={greenCircleImg} />
                )}
              </div>
            }
            rightAction={
              i > 0 && (
                <IconButton onClick={getDeleteObservationHander(i)}>
                  <Close />
                </IconButton>
              )
            }
            children={
              <Observation
                onUpdate={getUpdateFieldHandler(i)}
                observation={item}
              />
            }
          />
        ))}
        <div ref={bottomRef} />
      </Accordion>
      <Divider variant="middle" sx={{ background: '#604596' }} />
      <Button
        className="add-more-btn"
        disabled={
          !formik.values.observations[formik.values.observations.length - 1]
            .markFilled
        }
        onClick={handleAddMoreObservation}
      >
        + Add More Observations
      </Button>
      <div className="d-flex justify-content-between footer-content">
        <Button onClick={handleBack}>Back</Button>
        <Button
          type="submit"
          disabled={formik.values.observations.some((ob) => !ob.markFilled)}
        >
          Submit
        </Button>
      </div>
      <Modal
        show={showConfirmation}
        withIcon
        type={'success'}
        title=""
        modalClassName="checklist-modal"
        submitBtnText="Submit"
        cancelBtnText="Go back"
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </form>
  );
};
